'use client';

import { type ReactNode, useState } from 'react';
import { tv } from '../_lib/utils';
import { Anchor } from '../Anchor';
import { AnimatedIcon } from '../AnimatedIcon';
import { Container } from '../Container';
import { IconResolver, type IconType } from '../IconResolver';
import { Text } from '../Text';
import {
	CalendlyCtaActionPopup,
	type CtaAction,
	HubspotFormCtaAction,
	HubspotFormCtaActionPopup,
	MultipleChoiceCtaAction,
} from '../CtaActions';
import { AvatarGroup, type AvatarGroupProps } from '../AvatarGroup';
import { useMediaQuery } from '../_lib/useMediaQuery';
import { breakpoints } from '../_lib/breakpoints.mjs';
import type { SanityImage } from '../types';
import { Media } from '../Media';

const styles = tv({
	slots: {
		base: 'grid gap-8',
	},
	variants: {
		hovered: {
			left: {},
			right: {},
			none: {},
		},
		single: {
			true: {
				base: '',
			},
			false: {
				base: 'max-md:flex max-md:flex-col-reverse md:grid-cols-[calc(50%-1rem)_calc(50%-1rem)] md:items-start md:transition-all md:duration-slow md:ease-smooth',
			},
		},
	},
	compoundVariants: [
		{
			hovered: 'left',
			single: false,
			className: 'md:grid-cols-[calc(50%-4rem)_calc(50%+2rem)]',
		},
		{
			hovered: 'right',
			single: false,
			className: 'md:grid-cols-[calc(50%+2rem)_calc(50%-4rem)]',
		},
	],
});

const cardStyles = tv({
	slots: {
		base: 'isolate flex-1 rounded-lg flex min-h-[18.75rem] md:min-h-[25rem] lg:min-h-[30rem] transition-colors duration-fast',
		content: 'relative p-spacing-md flex flex-col transition-[opacity,transform] duration-fast ease-smooth w-full lg:p-spacing-xl',
		media:
			'relative -z-10 h-full [&_img]:h-full [&_img]:aspect-video -mt-spacing-md -mx-spacing-md mb-2 lg:[&_img]:aspect-auto lg:m-0 lg:absolute lg:top-0 lg:right-28 lg:w-[50%]',
		label: 'mb-5 lg:mb-6',
		icon: 'pointer-events-none shrink-0',
		button: "text-start before:content-[''] before:bottom-0 before:left-0 before:right-0 before:top-0 before:absolute",
	},
	variants: {
		hovered: {
			true: {},
			false: {},
		},
		styleType: {
			primary: {
				base: 'bg-button-primary-background text-text-white hover:data-hover-color:bg-button-primary-background-hover',
				label: 'text-text-white-secondary',
			},
			secondary: {
				base: 'bg-button-secondary-background text-text-primary border border-button-secondary-border hover:data-hover-color:bg-button-secondary-background-hover',
				label: 'text-text-secondary',
				icon: 'text-foreground-accent-primary',
			},
		},
		variant: {
			default: {},
			talk: {
				base: 'rounded-br-none',
			},
		},
	},
});

export type CTABlockCardProps = {
	className?: string;
	action: CtaAction;
	label?: string;
	title?: string;
	avatars?: AvatarGroupProps['items'];
	icon?: IconType;
	image?: SanityImage;
	video?: string;
	styleType?: 'primary' | 'secondary';
	variant?: 'default' | 'talk';
	hovered: boolean;
	single: boolean;
};

export type CTABlockProps = {
	className?: string;
	items: Omit<CTABlockCardProps, 'hovered' | 'single'>[];
};

export function CTABlock(props: CTABlockProps) {
	const [hoveredCardIndex, setHoveredCardIndex] = useState<number | null>(null);
	const single = props.items.length === 1 ? true : false;
	const { base } = styles({
		hovered: hoveredCardIndex === null ? 'none' : hoveredCardIndex % 2 ? 'left' : 'right',
		single,
	});

	return (
		<div className={props.className} data-component='CTABlock'>
			<Container>
				{props.items && (
					<div className={base()}>
						{props.items.map((card, index) => (
							<div
								key={`stagged-card-${card.label}-${index}-CTABlock`}
								className='self-stretch flex'
								onMouseEnter={() => setHoveredCardIndex(index)}
								onMouseLeave={() => setHoveredCardIndex(null)}
							>
								<CTABlockCard
									{...card}
									hovered={hoveredCardIndex === index ? true : hoveredCardIndex === null ? true : false}
									single={single}
								/>
							</div>
						))}
					</div>
				)}
			</Container>
		</div>
	);
}

function CTABlockCard(props: CTABlockCardProps) {
	const isMediumLayout = useMediaQuery(breakpoints.minLarge.query);

	const styleType = props.styleType ?? 'secondary';
	const variant = props.variant ?? 'default';
	const avatars = props.avatars ?? [];

	const { base, content, media, label, icon, button } = cardStyles({ hovered: props.hovered, styleType, variant });

	const [open, setOpen] = useState(false);

	let body: ReactNode = undefined;

	// link and modals
	if (
		props.action.type === 'link' ||
		props.action.type === 'calendly' ||
		(props.action.type === 'hubspotForm' && props.action.displayInModal)
	) {
		body = (
			<>
				<section className={`group/button group/avatarGroup flex flex-1 flex-col gap-5 lg:gap-6`}>
					{props.single && props.image && <Media className={media()} objectFit='contain' image={props.image} video={props.video} />}

					{avatars.length > 0 && <AvatarGroup className='self-start z-10' items={avatars} size={isMediumLayout ? '3xl' : 'xl'} />}

					{avatars.length === 0 && props.icon && (
						<div className='hidden bg-button-secondary-background self-start size-20 rounded-full md:flex md:items-center md:justify-center'>
							<IconResolver icon={props.icon} className='w-8 h-8' />
						</div>
					)}

					<div className='flex flex-1 flex-col gap-5 lg:gap-6 max-lg:justify-end lg:justify-between lg:flex-row lg:items-end md:transition-all md:duration-slow md:ease-smooth'>
						<div
							className={
								props.single ? `w-full` : `w-full transition-all duration-slow ease-smooth md:pr-0 hover:md:max-lg:pr-16 lg:max-w-[28rem]`
							}
						>
							{props.label && (
								<Text className={label()} styleType='label-md'>
									{props.label}
								</Text>
							)}

							{props.title && props.action.type === 'link' && (
								<>
									{props.action.href ? (
										<Text as='h2' styleType='heading-md'>
											<Anchor className={button()} href={props.action.href}>
												{props.title}
											</Anchor>
										</Text>
									) : (
										<Text styleType='heading-md'>{props.title}</Text>
									)}
								</>
							)}

							{props.title && props.action.type !== 'link' && (
								<Text as='h2' styleType='heading-md'>
									<button
										className={button()}
										onClick={() => {
											setOpen(true);
										}}
									>
										{props.title}
									</button>
								</Text>
							)}
						</div>

						<AnimatedIcon className={icon()} defaultEmpty={false} direction='topRight'>
							<IconResolver icon='arrow-up-right' className={isMediumLayout ? 'w-10 h-10' : 'w-8 h-8'} />
						</AnimatedIcon>
					</div>
				</section>

				{props.action.type === 'hubspotForm' && <HubspotFormCtaActionPopup open={open} setOpen={setOpen} {...props.action} />}
				{props.action.type === 'calendly' && <CalendlyCtaActionPopup open={open} setOpen={setOpen} {...props.action} />}
			</>
		);
	}

	// hubspotForm
	if (props.action.type === 'hubspotForm' && !props.action.displayInModal) {
		body = (
			<div className={`flex flex-1 flex-col lg:flex-row lg:gap-16 lg:items-end`}>
				<HubspotFormCtaAction {...props.action} />
			</div>
		);
	}

	// multiple-choice
	if (props.action.type === 'multiple-choice') {
		body = (
			<div className={`flex flex-1 flex-col justify-center`}>
				<MultipleChoiceCtaAction {...props.action} styleType={props.styleType} optionStyle={props.single ? 'square' : 'horizontal'} />
			</div>
		);
	}

	return (
		<article
			className={base({ className: props.className })}
			data-hover-color={props.action.type !== 'multiple-choice' ? 'true' : undefined}
			data-component='CTABlockCard'
		>
			<div className={content()}>{body}</div>
		</article>
	);
}
